import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  IconButton,
} from "@material-ui/core";
import moment from "moment";
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { Loading } from "../../components/shared/loading";
import { EditorCkMain } from "../../components/CkEditor";
import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";

import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SaveIcon from "@material-ui/icons/Save";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_TOTAL_IMAGES = 10;

export const EditArticle = () => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [date, setDate] = useState("");
  const [status, setStatus] = useState(1);
  const [oldImages, setOldImages] = useState([]);
  const [imageids, setImageIds] = useState("");
  const [images, setImages] = useState([]);
  const [err, setErr] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchArticle = async () => {
    const formdata = new FormData();
    formdata.append("article_id", id);
    try {
      const res = await axios.post("/api/get_single_article", formdata);
      if (res.data.status === true) {
        const d = res.data.data;
        setTitle(d.article_title || "");
        setContent(d.article_desc || "");
        setStatus(d.article_status || 1);
        const inputDate = d.article_published_date;
        const formatted = moment(inputDate, "DD-MM-YYYY HH:mm:ss").format(
          "YYYY-MM-DDTHH:mm"
        );
        setDate(formatted);
        setImageIds(d.article_images || "");
        setOldImages(
          (d.article_images || "")
            .split(",")
            .filter((s) => s.trim() !== "")
        );
        setLoading(false);
      } else {
        setLoading(false);
        toast.error("Could not load article");
      }
    } catch {
      setLoading(false);
      toast.error("Network error. Please try again.");
    }
  };

  useEffect(() => {
    fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const total =
      oldImages.length + images.length + e.target.files.length;
    if (total > MAX_TOTAL_IMAGES) {
      toast.error(`Maximum ${MAX_TOTAL_IMAGES} images allowed.`);
      e.target.value = "";
      return;
    }
    const arr = [];
    for (let i = 0; i < e.target.files.length; i++) {
      const file = e.target.files[i];
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPEG, PNG or WebP allowed.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: must be smaller than 5 MB.`);
        continue;
      }
      arr.push({ raw: file, preview: URL.createObjectURL(file) });
    }
    if (arr.length > 0) setImages((prev) => [...prev, ...arr]);
    e.target.value = "";
  };

  const removeNewImage = (preview) => {
    setImages((prev) => prev.filter((item) => item.preview !== preview));
  };

  const deleteOldImage = async (image) => {
    const formdata = new FormData();
    formdata.append("article_images", imageids);
    formdata.append("image_to_delete", image);
    formdata.append("article_id", id);
    try {
      const res = await axios.post("/api/delete_image", formdata);
      if (res.data.status === true) {
        toast.success(res.data.msg || "Image deleted");
        fetchArticle();
      } else {
        toast.error(res.data.msg || "Could not delete image");
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
  };

  const onSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr(0);
    if (!title.trim()) {
      setErr(1);
      toast.error("Please enter the article title.");
      return;
    }
    if (!date) {
      setErr(2);
      toast.error("Please select a published date.");
      return;
    }
    if (oldImages.length === 0 && images.length === 0) {
      setErr(3);
      toast.error("Please keep at least one image.");
      return;
    }
    if (!content || !content.trim()) {
      setErr(4);
      toast.error("Please enter the article content.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    images.forEach((i) => formData.append("article_images", i.raw));
    formData.append("article_id", id);
    formData.append("article_title", title.trim());
    formData.append("article_desc", content);
    formData.append("article_published_date", date);
    formData.append("article_status", status);
    formData.append("article_images", imageids);

    try {
      const res = await axios.post("/api/update_article", formData);
      setSubmitting(false);
      if (res.data.status === true) {
        toast.success(res.data.msg || "Article updated");
        navigate("/dash/allarticles");
      } else {
        toast.error(res.data.msg || "Something went wrong. Try again.");
      }
    } catch {
      setSubmitting(false);
      toast.error("Network error. Please try again.");
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <Helmet>
        <title>Edit Article - Admin | Arawinz Soft Solutions</title>
      </Helmet>

      <AdminPageHeader
        eyebrow="Articles"
        title="Edit article"
        subtitle="Update the article content, images, and metadata."
        backTo="/dash/allarticles"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Title"
            variant="outlined"
            error={err === 1}
            value={title}
            size="small"
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Published date"
            type="datetime-local"
            variant="outlined"
            value={date}
            size="small"
            error={err === 2}
            onChange={(e) => setDate(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Status"
            variant="outlined"
            value={status}
            size="small"
            onChange={(e) => setStatus(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={2}>Inactive</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Article images</Typography>
          <input
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleImageChange}
            id="article-images-edit"
            type="file"
          />
          <label htmlFor="article-images-edit" style={{ display: "block" }}>
            <Box className={classes.uploadBox}>
              <Box className={classes.uploadIconWrap}>
                <CloudUploadOutlinedIcon />
              </Box>
              <Box className={classes.uploadInfo}>
                <span>
                  <strong>Add more images</strong>
                </span>
                <Typography className={classes.uploadHint}>
                  JPEG, PNG or WebP, up to 5 MB each. Max {MAX_TOTAL_IMAGES} total.
                </Typography>
              </Box>
            </Box>
          </label>

          {oldImages.length > 0 && (
            <Box style={{ marginTop: 16 }}>
              <Typography className={classes.uploadHint} style={{ marginBottom: 8 }}>
                Existing images
              </Typography>
              <Grid container spacing={2}>
                {oldImages.map((image, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={image + idx}>
                    <Box
                      style={{
                        position: "relative",
                        borderRadius: 10,
                        overflow: "hidden",
                        border: "1px solid rgba(76,0,76,0.08)",
                      }}
                    >
                      <img
                        src={`/static/articles/${image}`}
                        alt={image}
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {oldImages.length > 1 && (
                        <IconButton
                          onClick={() => deleteOldImage(image)}
                          size="small"
                          aria-label="Delete image"
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            background: "rgba(255,255,255,0.95)",
                            color: "#dc2626",
                            border: "1px solid rgba(220,38,38,0.25)",
                          }}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {images.length > 0 && (
            <Box style={{ marginTop: 16 }}>
              <Typography className={classes.uploadHint} style={{ marginBottom: 8 }}>
                New images to upload
              </Typography>
              <Grid container spacing={2}>
                {images.map((img, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={img.preview + idx}>
                    <Box
                      style={{
                        position: "relative",
                        borderRadius: 10,
                        overflow: "hidden",
                        border: "1px solid rgba(155,59,155,0.3)",
                      }}
                    >
                      <img
                        src={img.preview}
                        alt={`new-${idx}`}
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      <IconButton
                        onClick={() => removeNewImage(img.preview)}
                        size="small"
                        aria-label="Remove image"
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          background: "rgba(255,255,255,0.95)",
                          color: "#dc2626",
                          border: "1px solid rgba(220,38,38,0.25)",
                        }}
                      >
                        <DeleteOutlineIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          {err === 3 && (
            <Typography style={{ color: "#dc2626", fontSize: "0.78rem", marginTop: 8 }}>
              Please keep at least one image.
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Content</Typography>
          <EditorCkMain desc={content} setDescr={(e) => setContent(e)} />
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button
          className={classes.secondaryBtn}
          onClick={() => navigate("/dash/allarticles")}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className={classes.primaryBtn}
          startIcon={!submitting && <SaveIcon />}
        >
          {submitting ? "Saving..." : "Save changes"}
        </Button>
      </Box>
    </div>
  );
};
